import { useState, useMemo, useCallback, useEffect } from "react";
import { useMonthlyGoal } from "./useMonthlyGoal";
import { useFunnelHealth, type FunnelSourceData } from "./useFunnelHealth";
import type { CurrentMonthMetrics } from "./useForecastData";
import {
  type SourceKey,
  type SourceRates,
  type MonthMetrics,
  type PaceStatus,
  type RateOverrides,
  SOURCE_KEYS,
  deriveRates,
  reverseCalculate,
  forwardProject,
  withGoal,
  computePaceStatus,
  paceProjection,
} from "../lib/revenuePlannerEngine";

// =============================================================================
// useRevenuePlanner — Full Planner State + Calculations
// =============================================================================
// PURPOSE: Combines funnel conversion rates, forecast month metrics, and
// revenue goal into a bidirectional planner with source toggles and sliders.
//
// All math is useMemo — instant recalculation on every interaction.
// No RPC calls during slider/toggle changes.
// =============================================================================

export function useRevenuePlanner(
  organizationId: string | null | undefined,
  currentMonthMetrics: CurrentMonthMetrics,
  targetMonth?: Date
) {
  // --- Data Sources ---
  const {
    goal,
    isLoading: goalLoading,
    saveGoal,
    deleteGoal,
  } = useMonthlyGoal(organizationId ?? null, targetMonth);

  // Current-month date range for funnel data — ensures leads_in and days_elapsed
  // are aligned to the same time window (prevents inflated leads/day from wider ranges)
  const currentMonthRange = useMemo(() => {
    const now = new Date();
    return {
      start: new Date(now.getFullYear(), now.getMonth(), 1),
      end: now,
    };
  }, []);
  const { bySource, isLoading: funnelLoading } = useFunnelHealth(organizationId, currentMonthRange);

  // --- Local State ---
  const [liveGoalInput, setLiveGoalInput] = useState<number>(0);
  const [inputOverrides, setInputOverrides] = useState<Partial<Record<SourceKey, number>>>({});
  const [rateOverrides, setRateOverrides] = useState<Partial<Record<SourceKey, RateOverrides>>>({});

  // --- Source Toggle Persistence ---
  // Storage key is org-specific so each org can have different source preferences
  const storageKey = organizationId ? `altara:revenue-planner:sources:${organizationId}` : null;

  const [activeSources, setActiveSources] = useState<SourceKey[]>(() => {
    if (!storageKey) return [...SOURCE_KEYS];
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored) as SourceKey[];
        // Validate that stored values are valid SourceKeys
        const valid = parsed.filter((k) => SOURCE_KEYS.includes(k));
        return valid.length > 0 ? valid : [...SOURCE_KEYS];
      }
    } catch {
      // Invalid JSON, fall back to all sources
    }
    return [...SOURCE_KEYS];
  });

  // Persist source toggles to localStorage when they change
  useEffect(() => {
    if (storageKey) {
      localStorage.setItem(storageKey, JSON.stringify(activeSources));
    }
  }, [storageKey, activeSources]);

  // --- Derived: Source Rates ---
  const funnelRates = useMemo((): Partial<Record<SourceKey, SourceRates>> => {
    const result: Partial<Record<SourceKey, SourceRates>> = {};
    for (const key of SOURCE_KEYS) {
      const src: FunnelSourceData | undefined = bySource[key];
      if (src) {
        result[key] = deriveRates({
          leads_in: src.leads_in ?? 0,
          booked: src.booked ?? 0,
          shown: src.shown ?? 0,
          closed: src.closed ?? 0,
          revenue: src.revenue ?? 0,
          cash: src.total_cash ?? src.cash ?? 0,
          attended: src.attended,
          total_signups: src.total_signups,
        });
      }
    }
    return result;
  }, [bySource]);

  // --- Derived: Month Metrics adapter ---
  const monthMetrics = useMemo(
    (): MonthMetrics => ({
      days_elapsed: currentMonthMetrics.days_elapsed,
      days_remaining: currentMonthMetrics.days_remaining,
      days_in_month: currentMonthMetrics.days_in_month,
      revenue_so_far: currentMonthMetrics.revenue_so_far,
      cash_so_far: currentMonthMetrics.cash_so_far,
    }),
    [currentMonthMetrics]
  );

  // --- Goal Value ---
  const goalAmount = goal?.revenue_goal ?? 0;
  // effectiveGoal: saved goal takes priority, else use whatever's typed live
  const effectiveGoal = goalAmount > 0 ? goalAmount : liveGoalInput;

  // --- Reverse Calc (Left Panel: "What We Need") ---
  // Uses full goal — shows ideal monthly average pace (benchmark)
  // "What Happens If" uses remaining days (recovery/scenario tool)
  const reverseCalc = useMemo(
    () => reverseCalculate(effectiveGoal, funnelRates, monthMetrics, activeSources),
    [effectiveGoal, funnelRates, monthMetrics, activeSources]
  );

  // --- Forward Projection (Right Panel: "What Happens If") ---
  const forwardProjection = useMemo(() => {
    const projection = forwardProject(
      inputOverrides,
      funnelRates,
      monthMetrics,
      activeSources,
      rateOverrides
    );
    return withGoal(projection, effectiveGoal);
  }, [inputOverrides, rateOverrides, funnelRates, monthMetrics, activeSources, effectiveGoal]);

  // --- Pace Status ---
  const paceStatus: PaceStatus = useMemo(
    () =>
      computePaceStatus(
        monthMetrics.cash_so_far,
        effectiveGoal,
        monthMetrics.days_elapsed,
        monthMetrics.days_in_month
      ),
    [monthMetrics, effectiveGoal]
  );

  const paceProjected = useMemo(
    () =>
      paceProjection(
        monthMetrics.cash_so_far,
        monthMetrics.days_elapsed,
        monthMetrics.days_in_month
      ),
    [monthMetrics]
  );

  // --- Actions ---
  const toggleSource = useCallback((key: SourceKey) => {
    setActiveSources((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  }, []);

  const setInputOverride = useCallback((key: SourceKey, value: number) => {
    setInputOverrides((prev) => ({ ...prev, [key]: value }));
  }, []);

  const clearOverride = useCallback((key: SourceKey) => {
    setInputOverrides((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const setRateOverride = useCallback(
    (key: SourceKey, field: keyof RateOverrides, value: number) => {
      setRateOverrides((prev) => ({
        ...prev,
        [key]: { ...(prev[key] ?? {}), [field]: value },
      }));
    },
    []
  );

  const clearRateOverride = useCallback((key: SourceKey, field?: keyof RateOverrides) => {
    setRateOverrides((prev) => {
      if (!field) {
        const next = { ...prev };
        delete next[key];
        return next;
      }
      const existing = prev[key];
      if (!existing) return prev;
      const next = { ...existing };
      delete next[field];
      return { ...prev, [key]: next };
    });
  }, []);

  const handleSaveGoal = useCallback(
    async (amount: number, notes?: string) => {
      const result = await saveGoal(amount, notes);
      return result;
    },
    [saveGoal]
  );

  return {
    // Goal
    goal,
    goalAmount,
    effectiveGoal,
    liveGoalInput,
    setLiveGoalInput,
    saveGoal: handleSaveGoal,
    deleteGoal,

    // Funnel data
    funnelRates,
    bySource, // raw funnel data with source-specific sub-columns
    monthMetrics,

    // Source toggles
    activeSources,
    toggleSource,

    // Input overrides (sliders)
    inputOverrides,
    setInputOverride,
    clearOverride,

    // Rate overrides (editable conversion rates)
    rateOverrides,
    setRateOverride,
    clearRateOverride,

    // Calculations
    reverseCalc,
    forwardProjection,

    // Pace
    paceStatus,
    paceProjected,

    // Loading
    isLoading: goalLoading || funnelLoading,
  };
}
